import {useEffect, useMemo, useRef, useState} from 'react'
import {User} from '../../../api/accessControl/User.ts'
import {GLASelector} from '../GLASelector'
import {GlAccount, useGLAccounts} from '../../../hooks/useGLAccounts.ts'
import {GLMapping, GLMappingRule} from '../../../lib/payrollGLMapping.ts'
import Loading from '../../general/Loading.tsx'

export interface PayrollGLMappingEditorProps {
    user: User
    mapping: GLMapping
    rules: GLMappingRule[]
    coverage: {allGlAccounts: {code: string; description: string}[]; unmappedGlAccounts: {code: string; description: string}[]} | null
    usedByCount: number
    providerName: string | null
    coverageLoadingMessage: string | null
    onUpsertRule: (code: string, description: string, glAccountId: string) => Promise<void>
    onRemoveRule: (code: string) => Promise<void>
}

const KEYWORD_TABLE: Array<{keywords: string[]; search: string}> = [
    {keywords: ['loon', 'salaris'], search: 'lonen en salarissen'},
    {keywords: ['loonkosten'], search: 'loonkosten'},
    {keywords: ['netto'], search: 'netto lonen'},
    {keywords: ['fiscus', 'loonheffing'], search: 'loonheffing'},
    {keywords: ['sociale lasten'], search: 'sociale lasten'},
    {keywords: ['pensioen'], search: 'pensioen'},
    {keywords: ['vakantie'], search: 'vakantie'},
    {keywords: ['reservering'], search: 'reservering'},
]

function normalizeText(text: string): string {
    return text.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim()
}

function computeSuggestion(description: string, accounts: GlAccount[]): GlAccount | null {
    if (accounts.length === 0) return null
    const normalized = normalizeText(description)

    for (const entry of KEYWORD_TABLE) {
        if (entry.keywords.some(kw => normalized.includes(kw))) {
            const match = accounts.find(a =>
                normalizeText(a.label_nl).includes(normalizeText(entry.search))
            )
            if (match) return match
        }
    }

    const descWords = normalized.split(/\s+/).filter(Boolean)
    if (descWords.length === 0) return null

    let bestAccount: GlAccount | null = null
    let bestScore = 0
    for (const account of accounts) {
        const accountWords = normalizeText(account.label_nl).split(/\s+/).filter(Boolean)
        const shared = descWords.filter(w => accountWords.includes(w)).length
        if (shared >= 1 && shared > bestScore) {
            bestScore = shared
            bestAccount = account
        }
    }
    return bestAccount
}

function GlMappingRow({
    code,
    description,
    isUnmapped,
    accounts,
    value,
    disabled,
    suggestion,
    autoApplied,
    usedByCount,
    onChange,
}: {
    code: string
    description: string
    isUnmapped: boolean
    accounts: GlAccount[]
    value: GlAccount | null
    disabled: boolean
    suggestion: GlAccount | null
    autoApplied: boolean
    usedByCount: number
    onChange: (account: GlAccount | null) => void
}) {
    return (
        <div
            className={`flex items-start gap-3 p-2 rounded-md ${isUnmapped ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-100'}`}
        >
            <span className="w-56 text-sm font-mono text-gray-700 shrink-0 pt-1">
                {code} — {description}
            </span>
            <div className="flex-1 space-y-1">
                <GLASelector
                    accounts={accounts}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    placeholder="Kies grootboekrekening"
                />
                {autoApplied && (
                    <span className="text-xs text-gray-400">Automatisch voorgesteld ✨</span>
                )}
                {!autoApplied && isUnmapped && suggestion && usedByCount > 1 && (
                    <button
                        type="button"
                        onClick={() => onChange(suggestion)}
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                    >
                        Suggestie: {suggestion.label_nl}
                    </button>
                )}
            </div>
        </div>
    )
}

export function PayrollGLMappingEditor({
    user,
    mapping,
    rules,
    coverage,
    usedByCount,
    providerName,
    coverageLoadingMessage,
    onUpsertRule,
    onRemoveRule,
}: PayrollGLMappingEditorProps) {
    const {glAccounts, isLoading: glAccountsLoading} = useGLAccounts(user)
    const [rowError, setRowError] = useState<string | null>(null)
    const hasConfirmedRef = useRef(false)
    const [autoAppliedCodes, setAutoAppliedCodes] = useState<Set<string>>(new Set())
    const [dismissedCodes] = useState<Set<string>>(new Set())
    const autoApplyFiredRef = useRef<string | null>(null)

    const accountsById = useMemo(() => {
        const map = new Map<string, GlAccount>()
        glAccounts.forEach(a => map.set(a.id, a))
        return map
    }, [glAccounts])

    const glAccountByCode = useMemo(() => {
        const map = new Map<string, string>()
        rules.forEach(r => map.set(r.externalGlAccountCode, r.glAccount))
        return map
    }, [rules])

    function requireConfirmIfShared(): boolean {
        if (usedByCount <= 1) return true
        if (hasConfirmedRef.current) return true
        const confirmed = window.confirm(
            `Gebruikt door ${usedByCount} klanten — wijzigingen gelden voor alle klanten bij de volgende import.\n\nWil je doorgaan?`
        )
        if (confirmed) hasConfirmedRef.current = true
        return confirmed
    }

    async function handleChange(
        code: string,
        description: string,
        account: GlAccount | null,
    ) {
        if (!requireConfirmIfShared()) return
        setRowError(null)
        if (autoAppliedCodes.has(code)) {
            setAutoAppliedCodes(prev => {
                const next = new Set(prev)
                next.delete(code)
                return next
            })
            dismissedCodes.add(code)
        }
        try {
            if (account) {
                await onUpsertRule(code, description, account.id)
            } else {
                await onRemoveRule(code)
            }
        } catch {
            setRowError('Koppelen mislukt. Probeer het opnieuw.')
        }
    }

    const guardKey = `${mapping.mappingId}:${coverage ? 'cov' : 'none'}`

    useEffect(() => {
        if (usedByCount > 1) return
        if (!coverage || glAccountsLoading || glAccounts.length === 0) return
        if (autoApplyFiredRef.current === guardKey) return
        autoApplyFiredRef.current = guardKey

        async function applyBatch() {
            const candidates = coverage!.unmappedGlAccounts.filter(a => {
                const alreadyMapped = glAccountByCode.has(a.code)
                return !alreadyMapped && !dismissedCodes.has(a.code)
            })

            const applied = new Set<string>()
            for (const gla of candidates) {
                const suggestion = computeSuggestion(gla.description, glAccounts)
                if (!suggestion) continue
                try {
                    await onUpsertRule(gla.code, gla.description, suggestion.id)
                    applied.add(gla.code)
                } catch {
                }
            }
            if (applied.size > 0) {
                setAutoAppliedCodes(prev => new Set([...prev, ...applied]))
            }
        }

        applyBatch()
    }, [guardKey, glAccountsLoading])

    const rows: {code: string; description: string}[] = coverage
        ? coverage.allGlAccounts
        : rules.map(r => ({code: r.externalGlAccountCode, description: r.externalGlAccountDescription}))

    return (
        <div>
            {usedByCount > 1 && (
                <div className="mb-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800">
                    Gebruikt door {usedByCount} klanten — wijzigingen gelden voor alle klanten bij de volgende import.
                </div>
            )}

            {coverageLoadingMessage !== null && (
                <div className="text-sm text-gray-500 px-2 py-3">
                    <Loading message={coverageLoadingMessage}/>
                </div>
            )}

            {coverageLoadingMessage === null && rows.length === 0 && !coverage && (
                <p className="text-sm text-gray-500 italic px-2 py-3">
                    Geen regels ingesteld voor deze koppeling.
                </p>
            )}

            {coverageLoadingMessage === null && rows.length === 0 && coverage && (
                <p className="text-sm text-gray-500 italic px-2 py-3">
                    Geen grootboekrekeningen gevonden.
                </p>
            )}

            {coverageLoadingMessage === null && rows.length > 0 && (
                <div className="space-y-1">
                    <div className="flex gap-3 px-2 pb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        <span className="w-56">{providerName ? `Grootboekrekening (${providerName})` : 'Grootboekrekening'}</span>
                        <span className="flex-1">Grootboekrekening (PocketCFO)</span>
                    </div>
                    {rows.map(({code, description}) => {
                        const glAccountId = glAccountByCode.get(code)
                        const value = glAccountId ? accountsById.get(glAccountId) ?? null : null
                        const isUnmapped = coverage ? !glAccountByCode.has(code) : false
                        const suggestion = isUnmapped ? computeSuggestion(description, glAccounts) : null
                        return (
                            <GlMappingRow
                                key={code}
                                code={code}
                                description={description}
                                isUnmapped={isUnmapped}
                                accounts={glAccounts}
                                value={value}
                                disabled={glAccountsLoading}
                                suggestion={suggestion}
                                autoApplied={autoAppliedCodes.has(code)}
                                usedByCount={usedByCount}
                                onChange={account => handleChange(code, description, account)}
                            />
                        )
                    })}
                    {rowError && <p className="text-xs text-red-600 px-2 pt-2">{rowError}</p>}
                </div>
            )}
        </div>
    )
}
